import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Icon,
  Text,
} from "@prismicio/editor-ui"
import { useRef, useState } from "react"

import type {
  OnboardingStep,
  OnboardingStepId,
  OnboardingTracking,
} from "../../service"
import { OnboardingStepDialog } from "./OnboardingStepDialog"

interface OnboardingProgressStepperProps {
  steps: OnboardingStep[]
  completedSteps: string[]
  onToggleStep: (step: OnboardingStep) => Promise<void>
  tracking: OnboardingTracking
  stepOverrides?: Partial<Record<OnboardingStepId, () => void>>
}

export function OnboardingProgressStepper(
  props: OnboardingProgressStepperProps,
) {
  const { steps, completedSteps, onToggleStep, tracking, stepOverrides } = props
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [activeStep, setActiveStep] = useState(steps[0])
  const dialogContentRef = useRef<HTMLDivElement>(null)

  if (!activeStep) return null

  const activeStepIndex = steps.findIndex(({ id }) => id === activeStep.id)

  function isStepComplete(step: OnboardingStep) {
    return completedSteps.includes(step.id)
  }

  function showStep(step: OnboardingStep) {
    setActiveStep(step)
    setDialogOpen(true)

    void tracking.track({
      event: "shared-onboarding:step-opened",
      stepId: step.id,
      stepTitle: step.title,
      source: tracking.source,
    })
  }

  async function toggle(isUndo = false) {
    if (!activeStep) return

    await onToggleStep(activeStep)
    if (!isUndo) {
      // fade transition to the next step
      dialogContentRef.current?.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 400,
      })
      const nextStep = getNextIncompleteStep()
      if (nextStep) {
        showStep(nextStep)
      } else {
        setDialogOpen(false)
      }
    }
  }

  function getNextIncompleteStep() {
    let nextStepIndex = activeStepIndex + 1
    while (nextStepIndex < steps.length) {
      const nextStep = steps[nextStepIndex]
      if (nextStep && !isStepComplete(nextStep)) return nextStep
      nextStepIndex++
    }
    return undefined
  }

  return (
    <>
      <OnboardingStepDialog
        contentRef={dialogContentRef}
        isOpen={isDialogOpen}
        step={activeStep}
        stepIndex={activeStepIndex}
        isStepComplete={isStepComplete(activeStep)}
        onClose={() => setDialogOpen(false)}
        onActionClick={toggle}
      />
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button
            size="large"
            color="grey"
            sx={{ width: "100%" }}
            renderStartIcon={StartIcon}
          >
            {completedSteps.length > 0 ? "Continue" : "Start now"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" minWidth={256} collisionPadding={8}>
          <DropdownMenuLabel>
            <Text variant="small" color="grey11" sx={{ marginInline: 8 }}>
              Progress
            </Text>
          </DropdownMenuLabel>
          {steps.map((step, index) => {
            const isCompleted = isStepComplete(step)

            return (
              <DropdownMenuItem
                key={step.id}
                onSelect={stepOverrides?.[step.id] ?? (() => showStep(step))}
                description={step.description}
                completed={isCompleted}
                endAdornment={
                  <Icon name="chevronRight" size="small" color="grey11" />
                }
              >
                {`${index + 1} ${step.title}`}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

function StartIcon() {
  return <Icon name="arrowForward" size="small" color="grey11" />
}
